import React from "react";
import {FormattedMessage} from "react-intl";
import {Dinero, isZero, toDecimal} from "dinero.js";
import {formatMoney} from "@/utils/MoneyUtils";
import {OrderTotals as Totals} from "@widgets/pos/order/hooks/UseOrderTotals";
import {Alert} from "peggirkit";

const money = (d: Dinero<number>): string => formatMoney(Number(toDecimal(d)));

type Props = {
    orderTotals: Totals,
};

const OrderTotals = ({orderTotals}: Props) => {
    const {totals, minimumOrder, onlinePayment} = orderTotals;

    return (
        <div className={"space-y-3"}>
            <dl className={"space-y-1 text-sm text-gray-600 dark:text-slate-300"}>
                <div className={"flex justify-between gap-3"}>
                    <dt><FormattedMessage id={"subtotal"}/></dt>
                    <dd className={"tabular-nums"}>{money(totals.subtotal)}</dd>
                </div>
                {!isZero(totals.deposit) && (
                    <div className={"flex justify-between gap-3"}>
                        <dt><FormattedMessage id={"deposit"}/></dt>
                        <dd className={"tabular-nums"}>{money(totals.deposit)}</dd>
                    </div>
                )}
                {!isZero(totals.supFee) && (
                    <div className={"flex justify-between gap-3"}>
                        <dt><FormattedMessage id={"supFee"}/></dt>
                        <dd className={"tabular-nums"}>{money(totals.supFee)}</dd>
                    </div>
                )}
                {totals.deliveryFee !== null && (
                    <div className={"flex justify-between gap-3"}>
                        <dt><FormattedMessage id={"deliveryFee"}/></dt>
                        <dd className={"tabular-nums"}>{money(totals.deliveryFee)}</dd>
                    </div>
                )}
                {!isZero(totals.tip) && (
                    <div className={"flex justify-between gap-3"}>
                        <dt><FormattedMessage id={"tip"}/></dt>
                        <dd className={"tabular-nums"}>{money(totals.tip)}</dd>
                    </div>
                )}
                <div
                    className={"flex justify-between gap-3 pt-2 text-base font-semibold text-gray-900 dark:text-white"}>
                    <dt><FormattedMessage id={onlinePayment === null ? "totalToPay" : "newTotal"}/></dt>
                    <dd className={"tabular-nums"}>{money(totals.total)}</dd>
                </div>
                {onlinePayment !== null && (
                    <div className={"flex justify-between gap-3"}>
                        <dt><FormattedMessage id={"alreadyPaidOnline"}/></dt>
                        <dd className={"tabular-nums"}>{money(onlinePayment.paidOnline)}</dd>
                    </div>
                )}
            </dl>

            {onlinePayment !== null && !isZero(onlinePayment.toPayInPerson) && (
                <Alert
                    type={"warning"}
                    title={
                        <FormattedMessage
                            id={"customerPaysExtra"}
                            values={{amount: money(onlinePayment.toPayInPerson)}}
                        />
                    }
                    content={<FormattedMessage id={"customerPaysExtraDescription"}/>}
                />
            )}

            {onlinePayment !== null && !isZero(onlinePayment.toRefund) && (
                <Alert
                    type={"danger"}
                    title={
                        <FormattedMessage
                            id={"customerGetsRefund"}
                            values={{amount: money(onlinePayment.toRefund)}}
                        />
                    }
                    content={<FormattedMessage id={"customerGetsRefundDescription"}/>}
                />
            )}

            {minimumOrder.status !== "OK" && (
                <Alert
                    type={"warning"}
                    title={
                        <>
                            {
                                minimumOrder.status === "NO_POSTAL_CODE" &&
                                <FormattedMessage id={"pos.minimum.noPostalCode"}/>
                            }
                            {
                                minimumOrder.status === "OUTSIDE_DELIVERY_AREA" &&
                                <FormattedMessage id={"pos.minimum.outsideArea"}/>
                            }
                            {
                                minimumOrder.status === "BELOW_MINIMUM" && (
                                    <FormattedMessage
                                        id={"pos.minimum.below"}
                                        values={{
                                            minimum: money(minimumOrder.minimum),
                                            remaining: money(minimumOrder.remaining),
                                        }}
                                    />
                                )
                            }
                        </>
                    }
                />
            )}
        </div>
    );
};

export default OrderTotals;